import {
  useState,
  useEffect,
  useRef,
  ReactNode,
  CSSProperties,
} from 'react'
import { motion } from 'framer-motion'

export type WalletCard = {
  id: string
  title: string
  subtitle?: string
  /** Header gradient/color for this card. */
  accent: string
  /** Optional override for header text color. */
  textColor?: string
  /** Optional icon shown in the header chrome. */
  icon?: ReactNode
  /** Optional label rendered in the gap between the active card and the bottom stack. */
  affordance?: string
  render: (active: boolean) => ReactNode
}

type Props = {
  cards: WalletCard[]
  defaultActiveId?: string
}

// Layout constants — tuned to read like the Apple Wallet card stack on a phone.
const SAFE_TOP = 14
const H_MARGIN = 16
const HEADER_HEIGHT = 64
const PEEK = 36            // top edge of frontmost bottom-stack card visible above bottom anchor
const COLLAPSED_OFFSET = 6 // gap between consecutive bottom-stack cards
const BOTTOM_ANCHOR = 18   // gap from container bottom to the frontmost stack card's visible top edge
const EXPANDED_OFFSET = 100 // vertical step between cards in the expanded fan
const HOLD_GAP = 80         // height of the "Hold Near Reader" affordance area
const TRANSITION_LOCK_MS = 650
// Compact card body when minimized — header + inner bottom padding so the rounded
// bottom corner reads as an intentional pill rather than a slab clipped by the viewport.
const COMPACT_CARD_HEIGHT = HEADER_HEIGHT + 18
// Taller preview height for the expanded fan — gives each card real presence
// when all of them are on screen at once (designed for ~4–6 total cards).
const EXPANDED_CARD_HEIGHT = 160

const SPRING_EXPAND = { type: 'spring' as const, stiffness: 300, damping: 32, mass: 1 }
const SPRING_COLLAPSE = { type: 'spring' as const, stiffness: 260, damping: 34, mass: 1 }

type CardPosition = { y: number; z: number }

export function WalletStack({ cards, defaultActiveId }: Props) {
  const [activeId, setActiveId] = useState<string>(defaultActiveId ?? cards[0]?.id)
  const [expanded, setExpanded] = useState(false)
  const [locked, setLocked] = useState(false)
  const [containerH, setContainerH] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const lockTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => setContainerH(el.clientHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => () => window.clearTimeout(lockTimer.current), [])

  const activeIdx = Math.max(0, cards.findIndex((c) => c.id === activeId))
  // Card height — large enough for content, leaves room for the hold-near-reader
  // gap and the bottom deck peek when collapsed.
  const cardHeight = Math.max(
    240,
    containerH - SAFE_TOP - HOLD_GAP - BOTTOM_ANCHOR - PEEK,
  )

  const runLocked = (fn: () => void) => {
    if (locked) return
    setLocked(true)
    fn()
    window.clearTimeout(lockTimer.current)
    lockTimer.current = window.setTimeout(() => setLocked(false), TRANSITION_LOCK_MS)
  }

  const handleCardTap = (idx: number) => {
    if (locked) return
    if (expanded) {
      runLocked(() => {
        setActiveId(cards[idx].id)
        setExpanded(false)
      })
    } else {
      // Any tap that bubbles to a card while collapsed expands the deck —
      // including tapping the active card's own header. The active card's
      // body stops propagation so inputs/QR/notes stay interactive.
      runLocked(() => setExpanded(true))
    }
  }

  // Tapping the gap area (between active card and deck) also expands, matching Apple Wallet.
  const handleGapTap = () => {
    if (locked || expanded) return
    runLocked(() => setExpanded(true))
  }

  const positions: CardPosition[] = cards.map((_, idx) => {
    if (expanded) {
      return { y: SAFE_TOP + idx * EXPANDED_OFFSET, z: 100 + idx }
    }
    if (idx === activeIdx) {
      return { y: SAFE_TOP, z: 500 }
    }
    // Bottom deck: order by declared sequence, frontmost = most visible / highest z.
    const stackPos = idx < activeIdx ? idx : idx - 1
    const totalStacked = cards.length - 1
    const fromFront = totalStacked - 1 - stackPos // 0 = frontmost
    return {
      y: containerH - BOTTOM_ANCHOR - PEEK - fromFront * COLLAPSED_OFFSET,
      z: 100 + (totalStacked - fromFront),
    }
  })

  const activeCard = cards[activeIdx]
  const ready = containerH > 0

  return (
    <div
      className="h-[100dvh] w-full"
      style={{
        background: 'var(--theme-background)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={containerRef}
        className="relative h-full w-full overflow-hidden"
      >
        {/* "Hold Near Reader" affordance — between active card and bottom deck */}
        <motion.div
          className="pointer-events-none absolute inset-x-0 flex flex-col items-center justify-center gap-2 text-center"
          style={{
            top: SAFE_TOP + cardHeight,
            height: HOLD_GAP,
            color: 'var(--theme-text-muted)',
          }}
          initial={false}
          animate={{
            opacity: expanded ? 0 : ready ? 1 : 0,
            filter: expanded ? 'blur(4px)' : 'blur(0px)',
          }}
          transition={{ type: 'spring', stiffness: 240, damping: 30 }}
        >
          {activeCard?.affordance && (
            <>
              <HoldNearReaderIcon />
              <div className="text-[13px] font-medium tracking-wide">
                {activeCard.affordance}
              </div>
            </>
          )}
        </motion.div>

        {/* Nanza wordmark — fades in only when the deck is expanded, sits
            behind the cards near the bottom of the viewport. */}
        <motion.div
          className="pointer-events-none absolute inset-x-0 flex justify-center"
          style={{ bottom: 24, zIndex: 0 }}
          initial={false}
          animate={{ opacity: expanded ? 1 : 0 }}
          transition={{ type: 'spring', stiffness: 240, damping: 30 }}
        >
          <img
            src="/nanza-logo-full-white.svg"
            alt="Nanza"
            style={{ width: '61vw' }}
            draggable={false}
          />
        </motion.div>

        {/* Tap-catcher for the gap area so users can tap anywhere below the active card to expand */}
        {!expanded && (
          <button
            type="button"
            aria-label="Expand wallet"
            onClick={handleGapTap}
            className="absolute inset-x-0 z-[50] cursor-pointer bg-transparent"
            style={{
              top: SAFE_TOP + cardHeight,
              height: HOLD_GAP,
            }}
          />
        )}

        {/* Cards */}
        {cards.map((card, idx) => {
          const pos = positions[idx]
          const isActive = !expanded && idx === activeIdx

          // Staggers — top of stack moves first on expand; selected first on collapse.
          const delay = expanded
            ? idx * 0.016
            : idx === activeIdx
              ? 0
              : Math.abs(idx - activeIdx) * 0.012

          // Three card heights:
          //   active   → full cardHeight (content is visible/scrollable)
          //   expanded → EXPANDED_CARD_HEIGHT (taller preview for the fan)
          //   minimized → COMPACT_CARD_HEIGHT (compact pill at the bottom stack)
          const targetHeight = isActive
            ? cardHeight
            : expanded
              ? EXPANDED_CARD_HEIGHT
              : COMPACT_CARD_HEIGHT

          return (
            <motion.div
              key={card.id}
              initial={false}
              animate={{
                y: pos.y,
                height: targetHeight,
                scale: 1,
              }}
              transition={{
                ...(expanded ? SPRING_EXPAND : SPRING_COLLAPSE),
                delay,
              }}
              style={
                {
                  position: 'absolute',
                  top: 0,
                  left: H_MARGIN,
                  right: H_MARGIN,
                  borderRadius: 24,
                  zIndex: pos.z,
                  // Card root uses its accent so inactive cards (minimized in
                  // the bottom stack or fanned out in expanded view) are full
                  // color. The body overlay below paints theme-surface only
                  // when the card is active to give content room to breathe.
                  background: card.accent,
                  color: 'var(--theme-text)',
                  overflow: 'hidden',
                  boxShadow: isActive
                    ? '0 22px 50px -18px rgba(0,0,0,0.55), 0 6px 18px -10px rgba(0,0,0,0.35)'
                    : '0 14px 30px -16px rgba(0,0,0,0.45), 0 3px 10px -6px rgba(0,0,0,0.3)',
                  willChange: 'transform',
                  opacity: ready ? 1 : 0,
                } as CSSProperties
              }
              onClick={() => handleCardTap(idx)}
            >
              <CardHeader card={card} />
              {/* Body — mounted for every card so per-card state survives switching */}
              <motion.div
                aria-hidden={!isActive}
                initial={false}
                animate={{ opacity: isActive ? 1 : 0 }}
                transition={{ duration: 0.22, delay: isActive ? 0.05 : 0 }}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: HEADER_HEIGHT,
                  bottom: 0,
                  pointerEvents: isActive ? 'auto' : 'none',
                  background: 'var(--theme-surface)',
                }}
                onClick={(e) => {
                  // When the card is active and the user clicks into the body,
                  // don't trigger the card's onClick (which would re-expand).
                  if (isActive) e.stopPropagation()
                }}
              >
                <div className="h-full overflow-hidden">
                  {card.render(isActive)}
                </div>
              </motion.div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

function CardHeader({ card }: { card: WalletCard }) {
  return (
    <div
      className="relative flex w-full items-center gap-3 px-5"
      style={{
        height: HEADER_HEIGHT,
        background: card.accent,
        color: card.textColor || '#fff',
        cursor: 'pointer',
      }}
    >
      {card.icon && (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
          {card.icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold leading-tight">
          {card.title}
        </div>
        {card.subtitle && (
          <div className="truncate text-xs opacity-80">{card.subtitle}</div>
        )}
      </div>
    </div>
  )
}

function HoldNearReaderIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12a7 7 0 0 1 14 0" />
      <path d="M8 12a4 4 0 0 1 8 0" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  )
}
